import { Search } from 'lucide-react-native';
import { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { RESIDENCY_OPTIONS } from '~/lib/residency';
import { colors, fontFamily, space, type } from '~/ui/tokens';
import { CountryFlag } from './ui/CountryFlag';

export function shortCountryCode(code: string): string {
  return ({ in: 'IND', gb: 'UK' } as Record<string, string>)[code] ?? code.toUpperCase();
}

interface Props {
  /** The confirmed nationality, if the traveller has chosen one. */
  value: string;
  onSelect: (code: string) => void;
  autoFocus?: boolean;
  maxListHeight?: number;
}

/**
 * Searchable country list for the lead guest's nationality. Keyboard: type to
 * filter, ArrowUp/ArrowDown to move, Enter to choose (the single match, an
 * exact match, or the highlighted row).
 */
export function NationalityList({ value, onSelect, autoFocus = true, maxListHeight = 260 }: Props) {
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState<number | null>(null);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term
      ? RESIDENCY_OPTIONS.filter(({ code, name }) => name.toLowerCase().includes(term) || code.includes(term) || shortCountryCode(code).toLowerCase().includes(term))
      : RESIDENCY_OPTIONS;
  }, [query]);

  function handleKey(event: { nativeEvent?: { key?: string }; key?: string; preventDefault?: () => void }) {
    const key = event.key ?? event.nativeEvent?.key;
    if (key === 'ArrowDown') {
      event.preventDefault?.();
      setHighlighted((index) => (index == null ? 0 : Math.min(index + 1, matches.length - 1)));
    } else if (key === 'ArrowUp') {
      event.preventDefault?.();
      setHighlighted((index) => (index == null ? matches.length - 1 : Math.max(index - 1, 0)));
    } else if (key === 'Enter') {
      const term = query.trim().toLowerCase();
      const exact = matches.find(({ code, name }) => code === term || name.toLowerCase() === term || shortCountryCode(code).toLowerCase() === term);
      const chosen = highlighted != null ? matches[highlighted] : exact ?? (matches.length === 1 ? matches[0] : null);
      if (chosen) {
        event.preventDefault?.();
        onSelect(chosen.code);
      }
    }
  }

  return (
    <View style={styles.menu}>
      <View style={styles.search}>
        <Search size={15} color={colors.inkFaint} />
        <TextInput
          value={query}
          onChangeText={(next) => { setQuery(next); setHighlighted(null); }}
          onKeyPress={handleKey}
          {...({ onKeyDown: handleKey } as object)}
          placeholder="Search country or code"
          placeholderTextColor={colors.inkFaint}
          accessibilityLabel="Search nationalities"
          autoFocus={autoFocus}
          returnKeyType="done"
          style={styles.searchInput}
        />
      </View>
      <ScrollView style={{ maxHeight: maxListHeight }} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
        {matches.length ? matches.map(({ code, name }, index) => (
          <Pressable
            key={code}
            accessibilityRole="button"
            accessibilityLabel={`${name}, ${shortCountryCode(code)}`}
            onPress={() => onSelect(code)}
            style={[styles.option, (value === code || highlighted === index) && styles.optionActive]}
          >
            <CountryFlag code={code} size={18} />
            <Text numberOfLines={1} style={styles.name}>{name}</Text>
            <Text style={styles.code}>{shortCountryCode(code)}</Text>
          </Pressable>
        )) : <Text style={styles.empty}>No countries found</Text>}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  menu: { width: '100%', backgroundColor: colors.card, overflow: 'hidden' },
  search: { minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: space.related, paddingHorizontal: space.internal, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  searchInput: { flex: 1, minWidth: 0, height: 44, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink, ...({ outlineStyle: 'none' } as object) },
  option: { height: 44, flexDirection: 'row', alignItems: 'center', gap: space.compact, paddingHorizontal: space.internal },
  optionActive: { backgroundColor: colors.lineSoft },
  name: { flex: 1, minWidth: 0, fontFamily: fontFamily.medium, ...type.bodyRegular, color: colors.ink },
  code: { fontFamily: fontFamily.medium, ...type.metadata, color: colors.inkSoft },
  empty: { padding: space.internal, fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
});
